import { type Market } from "@dotcom/markets";

import NewsGallerySection from "./NewsGallerySection";
import NewsImageSection from "./NewsImageSection";
import NewsImagesSection from "./NewsImagesSection";
import NewsTextSection from "./NewsTextSection";
import NewsVideoSection from "./NewsVideoSection";

import {
  type ParsedWwwNewsImagesSection,
  type ParsedWwwNewsGallerySection,
  type ParsedWwwNewsTextSection,
  type ParsedWwwNewsTextSectionWithInformation,
  type ParsedWwwNewsTextSectionWithQuote,
} from "~/common/data/getNewsArticleBySlug/queries";
import {
  type ParsedWwwNewsImageSection,
  type ParsedWwwNewsVideoSection,
} from "~/common/queries";
import { ModelApiKeys } from "~/common/types";

interface Props {
  readonly sections: Array<
    | ParsedWwwNewsImageSection
    | ParsedWwwNewsVideoSection
    | ParsedWwwNewsGallerySection
    | ParsedWwwNewsImagesSection
    | ParsedWwwNewsTextSection
    | ParsedWwwNewsTextSectionWithInformation
    | ParsedWwwNewsTextSectionWithQuote
  >;
  readonly market: Market;
  readonly isChina: boolean;
}

const Sections = ({ sections, market, isChina }: Props) => {
  return (
    <>
      {sections.map((section) => {
        if (section._modelApiKey === ModelApiKeys.WwwNewsImageSection) {
          return (
            <NewsImageSection
              key={section.id}
              imageMobile={section.imageMobile}
              imageTablet={section.imageTablet}
              imageDesktop={section.imageDesktop}
              sectionSlug={section.sectionSlug}
            />
          );
        }

        if (section._modelApiKey === ModelApiKeys.WwwNewsVideoSection) {
          return (
            <NewsVideoSection
              key={section.id}
              captionsUrl={section.captions?.url}
              videoMobile={section.videoMobile}
              videoTablet={section.videoTablet}
              videoDesktop={section.videoDesktop}
              posterUrlMobile={section.imageMobile?.url}
              posterUrlTablet={section.imageTablet?.url}
              posterUrlDesktop={section.imageDesktop?.url}
              sectionSlug={section.sectionSlug}
              market={market}
            />
          );
        }

        if (section._modelApiKey === ModelApiKeys.WwwNewsTextSection) {
          return (
            <NewsTextSection
              key={section.id}
              text={section.text}
              callToAction={section.callToAction}
              sectionSlug={section.sectionSlug}
              isChina={isChina}
              market={market}
            />
          );
        }

        if (section._modelApiKey === ModelApiKeys.WwwNewsTextSectionWithQuote) {
          return (
            <NewsTextSection
              key={section.id}
              market={market}
              text={section.text}
              callToAction={section.callToAction}
              quote={section.quote}
              quotee={section.quotee}
              sectionSlug={section.sectionSlug}
              isChina={isChina}
            />
          );
        }

        if (
          section._modelApiKey ===
          ModelApiKeys.WwwNewsTextSectionWithInformation
        ) {
          return (
            <NewsTextSection
              key={section.id}
              market={market}
              text={section.text}
              callToAction={section.callToAction}
              info={section.info}
              sectionSlug={section.sectionSlug}
              isChina={isChina}
            />
          );
        }

        if (section._modelApiKey === ModelApiKeys.WwwNewsGallerySection) {
          return (
            <NewsGallerySection
              key={section.id}
              imagesMobile={section.imagesMobile}
              imagesTablet={section.imagesTablet}
              imagesDesktop={section.imagesDesktop}
              imagesWithCaption={section.imagesWithCaptions}
              sectionSlug={section.sectionSlug}
            />
          );
        }

        if (section._modelApiKey === ModelApiKeys.WwwNewsImagesSection) {
          return (
            <NewsImagesSection
              key={section.id}
              firstImageText={section.firstImageText}
              firstImageMobile={section.firstImageMobile}
              firstImageTablet={section.firstImageTablet}
              firstImageDesktop={section.firstImageDesktop}
              secondImageText={section.secondImageText}
              secondImageMobile={section.secondImageMobile}
              secondImageTablet={section.secondImageMobile}
              secondImageDesktop={section.secondImageDesktop}
              sectionSlug={section.sectionSlug}
            />
          );
        }

        return null;
      })}
    </>
  );
};

export default Sections;
